/**
 * @file Print Packages related fragments.
 */

import gql from 'graphql-tag';

import {
  printProductSizeServiceDetails,
  printProductSizeBaseDetails,
  printProductSizeRoesGraphicNodeDetails,
} from './print-product';
import { printPartnerOrderServiceDetails } from './print-partner';
import { VendorDetails } from './account';

export const PrintPackageDetails = gql`
  fragment PrintPackageDetails on PrintPackage {
    id
    name
    price {
      value
      currency
    }
    account {
      ...VendorDetails
    }
    items(slice: { limit: 9999 }) {
      total
      records {
        id
        type {
          id
          name
          active
          min_price {
            value
            currency
          }
        }
        custom_print {
          id
          name
          type
          preview_image {
            id
            url
          }
          photos_no
          background_image {
            id
            url
          }
          account {
            id
            logo_image {
              id
              url
            }
          }
          price {
            value
            currency
          }
          custom_print_caption_enabled
          shipping_required
          shipping_enabled
          card {
            id
            card_side_groups {
              records {
                id
                card_sides {
                  records {
                    id
                    card_template {
                      id
                      print_size {
                        id
                        name
                      }
                      print_product {
                        id
                        name
                        print_partner {
                          id
                          name
                        }
                        sizes(slice: { limit: 999 }) {
                          records {
                            id
                            print_size {
                              id
                            }
                            price {
                              value
                              currency
                            }
                            min_price {
                              value
                              currency
                            }
                            services {
                              total
                              records {
                                ...printProductSizeServiceDetails
                              }
                            }
                            roes_info {
                              layout_file
                              graphic_nodes(slice: { limit: 99 }) {
                                total
                                records {
                                  ...printProductSizeRoesGraphicNodeDetails
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        print_product_configuration {
          print_product {
            id
            name
            is_standard
            card {
              id
            }
          }
          print_product_size {
            id
            print_size {
              id
              name
            }
            price {
              value
              currency
            }
            min_price {
              value
              currency
            }
            services {
              total
              records {
                ...printProductSizeServiceDetails
              }
            }
            roes_info {
              layout_file
              graphic_nodes(slice: { limit: 99 }) {
                total
                records {
                  ...printProductSizeRoesGraphicNodeDetails
                }
              }
            }
          }
          print_product_size_services {
            total
            records {
              id
              print_product_size_service {
                ...printProductSizeServiceDetails
              }
            }
          }
          print_product_size_roes_graphic_node {
            ...printProductSizeRoesGraphicNodeDetails
          }
        }
        tag_template {
          __typename
          ... on PictureDayTagTemplate {
            id
            name
          }
        }
        quantity
        is_optional
        album_folder_print_package_items(slice: { limit: 1 }) {
          total
          records {
            id
          }
        }
      }
    }
    print_partner {
      id
      name
    }
    digital_downloads_mode
    digital_downloads_upsell_price {
      value
      currency
    }
    print_order_configuration {
      print_partner_order_services {
        total
        records {
          id
          print_partner_order_service {
            ...printPartnerOrderServiceDetails
          }
        }
      }
      total_price {
        value
        currency
      }
    }
    pricing_category {
      id
      name
    }
    pricing_categories {
      total
      records {
        id
        pricing_category {
          id
          name
        }
      }
    }
    print_backgrounds(slice: { limit: 999 }) {
      total
      records {
        id
        print_background {
          id
          name
          type
          asset_image {
            id
            url
            width
            height
          }
        }
      }
    }
  }
  ${printProductSizeServiceDetails}
  ${printPartnerOrderServiceDetails}
  ${printProductSizeRoesGraphicNodeDetails}
  ${VendorDetails}
`;

export const AlbumPrintPackageDetails = gql`
  fragment AlbumPrintPackageDetails on PrintPackage {
    id
    album_print_packages {
      total
      records {
        id
        album {
          id
          name
        }
      }
    }
  }
`;

export const PrintPackageForPurchaseDetails = gql`
  fragment PrintPackageForPurchaseDetails on PrintPackage {
    id
    name
    price {
      value
      currency
    }
    discount_price {
      value
      currency
    }
    digital_downloads_mode
    digital_downloads_upsell_price {
      value
      currency
    }
    print_package_bundle_discount_price {
      value
      currency
    }
    print_backgrounds(slice: { limit: 999 }) {
      total
      records {
        id
        print_background {
          id
          name
          type
          asset_image {
            id
            url
            width
            height
          }
        }
      }
    }
    account {
      ...VendorDetails
    }
    print_order_configuration {
      print_partner_order_services {
        total
        records {
          id
          print_partner_order_service {
            ...printPartnerOrderServiceDetails
          }
        }
      }
      total_price {
        value
        currency
      }
    }
    items(slice: { limit: 99 }) {
      total
      records {
        id
        quantity
        type {
          id
          name
        }
        album_folder_tag {
          ... on CustomAlbumFolderTag {
            id
            name
          }
        }
        price {
          value
          currency
        }
        discount_price {
          value
          currency
        }
        is_optional
        custom_print {
          id
          name
          type
          preview_image {
            id
            url
          }
          photos_no
          background_image {
            id
            url
          }
          account {
            id
            logo_image {
              id
              url
            }
          }
          price {
            value
            currency
          }
          custom_print_caption_enabled
          shipping_required
          shipping_enabled
          card {
            id
            card_side_groups {
              records {
                id
                side
                card_sides {
                  records {
                    id
                    card_template {
                      id
                      print_size {
                        id
                        name
                      }
                      print_product {
                        id
                        print_size {
                          id
                          name
                        }
                        print_partner {
                          id
                          name
                        }
                        sizes(slice: { limit: 999 }) {
                          records {
                            id
                            print_size {
                              id
                              name
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        print_product_configuration {
          print_product {
            id
            name
            is_standard
            card {
              id
            }
            preview_asset_image {
              id
              url
            }
          }
          print_product_size {
            ...printProductSizeBaseDetails
          }
          print_product_size_services {
            total
            records {
              id
              print_product_size_service {
                ...printProductSizeServiceDetails
              }
            }
          }
          print_product_size_roes_graphic_node {
            ...printProductSizeRoesGraphicNodeDetails
          }
        }
        album_folder_print_package_items(slice: { limit: 99 }) {
          total
          records {
            id
            album_folder_print_package {
              album_folder {
                id
              }
            }
            price {
              value
              currency
            }
            discount_price {
              value
              currency
            }
            album_folder_tag {
              ... on CustomAlbumFolderTag {
                id
                name
              }
            }
          }
        }
      }
    }
  }
  ${printProductSizeBaseDetails}
  ${printProductSizeServiceDetails}
  ${printPartnerOrderServiceDetails}
  ${printProductSizeRoesGraphicNodeDetails}
  ${VendorDetails}
`;
